import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const CandidateCard = ({ candidate, isSelected, onSelect, onStatusChange, onViewDetails, viewMode }) => {
  const getStatusColor = (status) => {
    const colors = {
      'Applied': 'bg-accent/10 text-accent border-accent/20',
      'Under Review': 'bg-warning/10 text-warning border-warning/20',
      'Shortlisted': 'bg-primary/10 text-primary border-primary/20',
      'Interview Scheduled': 'bg-success/10 text-success border-success/20',
      'Rejected': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getMatchScoreColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-primary';
    if (score >= 60) return 'text-warning';
    return 'text-error';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-4 hover:shadow-warm-md transition-smooth ${
      isSelected ? 'ring-2 ring-primary' : ''
    }`}>
      <div className="flex items-start gap-4">
        <Checkbox
          id={`candidate-${candidate?.id}`}
          checked={isSelected}
          onChange={() => onSelect(candidate?.id)}
        />
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
          <Image
            src={candidate?.photo}
            alt={candidate?.photoAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-semibold text-card-foreground line-clamp-1">
                {candidate?.name}
              </h3>
              <p className="text-sm text-muted-foreground line-clamp-1">{candidate?.qualification}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(candidate?.status)}`}>
                {candidate?.status}
              </span>
              <div className={`text-lg font-bold ${getMatchScoreColor(candidate?.matchScore)}`}>
                {candidate?.matchScore}%
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Briefcase" size={14} />
              <span className="line-clamp-1">{candidate?.experience}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span className="line-clamp-1">{candidate?.location}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground col-span-2">
              <Icon name="Calendar" size={14} />
              <span>Applied: {formatDate(candidate?.appliedDate)}</span>
            </div>
          </div>

          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {candidate?.skills?.slice(0, 4)?.map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-muted rounded text-xs text-card-foreground"
                >
                  {skill}
                </span>
              ))}
              {candidate?.skills?.length > 4 && (
                <span className="px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                  +{candidate?.skills?.length - 4} more
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewDetails(candidate)}
              iconName="Eye"
              iconPosition="left"
            >
              View Details
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => onStatusChange(candidate?.id, 'Shortlisted')}
              iconName="CheckCircle"
              iconPosition="left"
            >
              Shortlist
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onStatusChange(candidate?.id, 'Rejected')}
              iconName="XCircle"
              iconPosition="left"
            >
              Reject
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateCard;